import {
    useClickTracking,
    useImpressionTracking,
} from "@foundation/with-tracking";

import type { ModuleTracking, TrackingAttributes } from "~types/lineup";

interface TrackingProps {
    abbaExperiments?: string;
    storyId?: string;
    storyCount?: number;
    trackingType?: string;
    moduleTracking?: ModuleTracking;
    skipClickTracking?: boolean;
    skipImpressionTracking?: boolean;
    isPhoenix?: boolean;
    moduleIndex?: number;
    moduleTreatment?: string;
    renderingLocation?: string;
    zoneName?: string;
}

/*
 * Utility function to track impression and click events on modules.
 * Impression Tracking: Tracks when a component/module is visible/scrolled in end-user viewport
 * Click Tracking: Track click event on a component/module
 *
 * For both above types of tracking, data is set at component root as data attributes.
 * This utility simplifies those attribute setup for components, by preparing required attributes
 * based on data.
 * */
export default function trackStoriesInModules(
    options: TrackingProps
): TrackingAttributes {
    const {
        storyId,
        storyCount,
        trackingType,
        moduleTracking,
        skipClickTracking,
        skipImpressionTracking,
        isPhoenix = false,
        moduleTreatment = "",
    } = options;

    if (!moduleTracking || (skipClickTracking && skipImpressionTracking)) {
        return {};
    }

    const trackingAttributes: TrackingAttributes = {};

    /* [GK] Utility used inside react components, yet
     * lint complains about violating react hook rules. */
    /* eslint-disable react-hooks/rules-of-hooks */
    if (!skipClickTracking) {
        /* eslint-disable inclusive-language/use-inclusive-words */
        // mapped based on https://bbgithub.dev.bloomberg.com/Consumer-Web/sputnik/blob/master/src/mapping/google.json

        const { trackClick } = useClickTracking(
            isPhoenix
                ? {
                      clickData: {
                          action: moduleTracking?.title,
                          category: "hp-modules-click",
                          label: moduleTreatment,
                      },
                      findLinkTag: true,
                      elementClickDataAttributes: [
                          "data-link-type",
                          "data-link-index",
                          "data-link-related-type",
                          "data-link-related-index",
                      ],
                      transformElementAttributes: (attrs = {}) => {
                          let linkType = "";

                          if (
                              (attrs["data-link-type"] &&
                                  attrs["data-link-type"] !== "Story") ||
                              attrs["data-link-related-type"]
                          ) {
                              const storyIndexLabel =
                                  attrs["data-link-type"] &&
                                  attrs["data-link-type"] !== "Story"
                                      ? attrs["data-link-type"]
                                      : attrs["data-link-related-type"];

                              linkType = `${storyIndexLabel}-`;
                          }

                          const linkIndex =
                              attrs["data-link-index"] &&
                              attrs["data-link-related-index"]
                                  ? `${attrs["data-link-index"]}.${attrs["data-link-related-index"]}`
                                  : attrs["data-link-index"] || "";

                          const storyIndex = `${linkType}${linkIndex}`;

                          if (!storyIndex) {
                              return {} as Record<string, string>;
                          }

                          return {
                              customDimensions: {
                                  story_index: storyIndex,
                              },
                          } as unknown as Record<string, string>;
                      },
                  }
                : {
                      clickData: {
                          action: "click",
                          category: "Modules",
                          data: {
                              abbaId: moduleTracking?.abbaId,
                              moduleId: moduleTracking?.id,
                              moduleTitle: moduleTracking?.title,
                              storyId,
                          },
                      },
                  }
        );

        trackingAttributes.onClick = (event) => {
            const targetElement = event.target as Element;

            /**
             * Restrict tracking to links and link contents
             */
            if (
                targetElement?.matches?.("a") ||
                targetElement?.matches?.("a *")
            ) {
                trackClick(event);
            }
        };
    }

    if (!skipImpressionTracking) {
        const { containerRef } = useImpressionTracking();

        trackingAttributes.ref = containerRef;

        /* eslint-disable inclusive-language/use-inclusive-words */
        // mapped based on https://bbgithub.dev.bloomberg.com/Consumer-Web/sputnik/blob/master/src/mapping/bloomberg.json
        if (storyId) trackingAttributes["data-tracking-id"] = storyId;

        if (trackingType) {
            trackingAttributes["data-tracking-type"] = trackingType;
        }

        if (moduleTracking?.title) {
            trackingAttributes["data-tracking-title"] = moduleTracking?.title;
        }

        if (moduleTracking?.id) {
            trackingAttributes["data-parent-id"] = moduleTracking?.id;
        }

        if (!isNaN(Number(storyCount))) {
            trackingAttributes["data-story-count"] = storyCount;
        }

        trackingAttributes["data-abba-id"] = moduleTracking?.abbaId;
        trackingAttributes["data-abba-experiments"] =
            moduleTracking?.abbaExperiments;

        if (moduleTracking?.id) {
            trackingAttributes["data-module-id"] = moduleTracking?.id;
        }
    }

    return trackingAttributes;
}
